import { z, ZodType, ZodTypeDef } from 'zod';
import { Tag } from '~/components/ui/tag-input';
import { ChatTool } from './chat';

type ConfigBase = {
  key: string | number | symbol;
  description?: string;
  label: string;
  type: string;
  optional?: boolean;
  nullable?: boolean;
  nullish?: boolean;
};

type TextConfig = ConfigBase & {
  type: 'text';
  value: string;
  placeholder?: string;
};

type RangeConfig = ConfigBase & {
  type: 'number';
  value: number[];
  min?: number;
  max?: number;
  step?: number;
};

type SelectConfig = ConfigBase & {
  type: 'select';
  value: string;
  options?: string[];
  default?: string;
  placeholder?: string;
};

type CheckboxConfig = ConfigBase & {
  type: 'checkbox';
  value: boolean;
};

type ToolsConfig = ConfigBase & {
  type: 'tools';
  value: ChatTool[];
};

type TagsConfig = ConfigBase & {
  type: 'tags';
  value: Tag[];
};

export type Config = TextConfig | RangeConfig | SelectConfig | CheckboxConfig | ToolsConfig | TagsConfig;

type ZodTypeDefWithInternals = ZodTypeDef & {
  type: ZodType<any, any, any>;
  typeName:
    | 'ZodString'
    | 'ZodNumber'
    | 'ZodBoolean'
    | 'ZodDate'
    | 'ZodEnum'
    | 'ZodLiteral'
    | 'ZodNull'
    | 'ZodUndefined'
    | 'ZodObject'
    | 'ZodArray'
    | 'ZodUnion'
    | 'ZodIntersection'
    | 'ZodTuple'
    | 'ZodRecord'
    | 'ZodMap'
    | 'ZodSet'
    | 'ZodFunction'
    | 'ZodLazy'
    | 'ZodLiteral'
    | 'ZodPromise'
    | 'ZodEffects'
    | 'ZodOptional'
    | 'ZodNullable'
    | 'ZodNativeEnum';
  defaultValue?: any;
  description?: string;
  values?: string[];
  options?: any[];
};

export function formFieldsFromZodSchema<
  InputSchema extends z.ZodEffects<Schema, any, any> | Schema,
  Schema extends z.ZodObject<z.ZodRawShape, any>,
  ExcludedFields extends Array<keyof Schema['shape']>,
>({
  excludedFields = [] as any as ExcludedFields,
  schema: schemaOrEffect,
  overides = {},
  defaults = {},
}: {
  schema: InputSchema;
  excludedFields?: ExcludedFields;
  overides?: Partial<Record<keyof Schema['shape'], Partial<Config>>>;
  // TODO finish typing this
  defaults?: Partial<Record<keyof Schema['shape'], any>>;
}): Config[] {
  const fields: Config[] = [];
  // @ts-expect-error use of internal types
  const schema = schemaOrEffect?._def?.schema?._def.shape() ?? schemaOrEffect.shape;
  for (const key of Object.keys(schema) as typeof excludedFields) {
    if (excludedFields.includes(key)) continue;
    const isOptional = schema[key]?._def.typeName === 'ZodOptional';
    const isNullable = schema[key]?._def.typeName === 'ZodNullable';
    const isNullish = schema[key]?._def.typeName === 'ZodNullish';
    const fieldSchema: ZodTypeDefWithInternals = schema[key]?._def.innerType?._def ?? schema[key]?._def;

    const [min, max] =
      fieldSchema.typeName === 'ZodNumber'
        ? [
            // @ts-expect-error need type guard for min and max
            fieldSchema.checks.find((check) => check.kind === 'min')?.value,
            // @ts-expect-error need type guard for min and max
            fieldSchema.checks.find((check) => check.kind === 'max')?.value,
          ]
        : [undefined, undefined];

    fields.push({
      key,
      label: (key as string).replace(/_/gu, ' ').replace(/\b\w/gu, (letter) => letter.toUpperCase()),
      description: fieldSchema.description ?? '',
      optional: isOptional,
      nullable: isNullable,
      nullish: isNullish,
      placeholder: ['ZodEnum', 'ZodUnion'].includes(fieldSchema.typeName) ? 'Select an option' : undefined,
      options:
        fieldSchema.typeName === 'ZodEnum'
          ? fieldSchema.values
          : fieldSchema.typeName === 'ZodUnion'
            ? fieldSchema.options?.map((option) => option._def.value)
            : undefined,
      value: defaults[key] ?? fieldSchema.defaultValue ?? undefined,
      type: inputConfigFromZodType(fieldSchema),
      min,
      max,
      step:
        fieldSchema.typeName === 'ZodNumber' && min !== undefined && max !== undefined
          ? min >= -2 && max <= 2
            ? 0.1
            : 1
          : undefined,
      ...overides[key],
    });
  }
  return fields;
}

// Example function to map Zod schema types to HTML input types
export function inputConfigFromZodType(fieldSchema: ZodTypeDefWithInternals) {
  if (fieldSchema.typeName === 'ZodNumber') {
    return 'number';
  }
  if (
    fieldSchema.typeName === 'ZodUnion' &&
    fieldSchema.options?.every((option) => option._def.typeName === 'ZodLiteral')
  ) {
    return 'select';
  }
  if (fieldSchema.typeName === 'ZodEnum' || fieldSchema.typeName === 'ZodNativeEnum') {
    return 'select';
  }
  if (fieldSchema.typeName === 'ZodBoolean') {
    return 'checkbox';
  }
  if (fieldSchema.typeName === 'ZodArray' && fieldSchema.type._def.typeName === 'ZodString') {
    return 'tags';
  }

  return 'text'; // Default to text input type
}
